import React from 'react';
import styled from 'styled-components';
import { ButtonText, CalculationBreakdownList, Input, Text } from 'components';
import EmployeePayoutAccordion, { IEmployeePayoutAccordionHeader } from 'components/Calculator/EmployeePayoutAccordion/EmployeePayoutAccordion';
import { lineHeight, theme, toRem } from 'design';
import { DisbursementMethod, ICalculatorRules, IEmployerSite, IPayoutOverviewTotalsDisplay, ITipCalculatorPayee, SiteRole, TipCalculatorPayeeErrorType } from 'types';
import { centsToDollarsDisplay } from 'utils';
import { roleSiteDisplay } from 'utils/employee';

export interface IEmployeeListItemProps {
  currentCalculatorRules?: ICalculatorRules;
  className?: string;
  dataCy?: string;
  includedPayouts: IPayoutOverviewTotalsDisplay;
  isLoading?: boolean;
  maxTipAmount?: number;
  onCashEdit?: () => void;
  onEditHoldback?: (payee: ITipCalculatorPayee) => void;
  onNoteChange?: (payeeId: string, note: string) => void;
  onConnectEmployee?: (payee: ITipCalculatorPayee) => void;
  currentSite?: IEmployerSite;
  payee: ITipCalculatorPayee;
  creditCardFee?: number;
}

const Layout = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;

  &:not(:last-child) {
    margin-bottom: ${toRem(theme.space.lg)};
  }
`;

const EditHoldbackTrigger = styled(ButtonText)`
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  color: ${theme.color.primary[600]};
  text-decoration: none;
`;

const InlineButton = styled.button`
  display: inline-block;
  padding: 0;
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
  background: none;
  border: none;
  color: ${theme.color.primary[600]};
  outline: none;
  margin-left: ${toRem(theme.space.xs)};
`;

const NoteInput = styled(Input)`
  flex-shrink: 0;
`;

const NoteWrapper = styled.div`
  margin: ${toRem(theme.space.lg)} 0;
  padding-top: ${toRem(theme.space.lg)};
  border-top: 1px solid ${theme.color.neutral[300]};
`;

const buildHeader = (payee: ITipCalculatorPayee, isAdmin: boolean, maxTipAmount?: number, onConnectEmployee?: (payee: ITipCalculatorPayee) => void, onCashEdit?: () => void): IEmployeePayoutAccordionHeader | null => {
  if (!payee.errors?.length) return null;

  const instantDisbursement = payee.employee?.disbursementMethod === DisbursementMethod.INSTANT;
  let severity: 'info' | 'warning' = 'info';

  const messages = payee.errors.map((error, i) => {
    switch (error.type) {
      case TipCalculatorPayeeErrorType.AUTO_CLOCKOUT:
        return <Text key={`msg-${i}`}>Auto clock out detected. Please verify time worked.</Text>;
      case TipCalculatorPayeeErrorType.USER_SETUP_NOT_COMPLETE:
        severity = 'warning';
        return (
          <Text key={`msg-${i}`}>
            User setup not complete.
            {isAdmin ? <InlineButton onClick={() => onConnectEmployee?.(payee)}>Click here to complete setup.</InlineButton> : ' Contact your site admin to complete setup.'}
          </Text>
        );
      case TipCalculatorPayeeErrorType.NEGATIVE_NET_PAYMENT:
        severity = 'warning';
        return (
          <Text key={`msg-${i}`}>
            The payment must be greater than $0.00.
            {payee.cash > 0 ? <InlineButton onClick={onCashEdit}>Click here to adjust the cash payout amount.</InlineButton> : ` Please adjust tip amount${instantDisbursement ? ' or holdback' : ''} to resolve.`}
          </Text>
        );
      case TipCalculatorPayeeErrorType.MAX_PAYMENT_EXCEEDED:
        severity = 'warning';
        return (
          <Text key={`msg-${i}`}>
            Payment exceeds this site's maximum payment amount of {centsToDollarsDisplay(maxTipAmount)}. Please adjust tip amount{instantDisbursement ? ' or holdback' : ''} to resolve.
          </Text>
        );
      case TipCalculatorPayeeErrorType.INELIGIBLE_SELF_PAY:
        severity = 'warning';
        return <Text key={`msg-${i}`}>You are not eligible to receive tips.</Text>;
      case TipCalculatorPayeeErrorType.INELIGIBLE_PAYABLE:
        severity = 'warning';
        return <Text key={`msg-${i}`}>This user role ({roleSiteDisplay(payee.employee.role)}) is ineligible to receive tips.</Text>;
      default:
        return null;
    }
  });

  return { messages, severity };
};

const EmployeeListItem: React.FC<IEmployeeListItemProps> = ({
  currentCalculatorRules,
  className = 'review-list-item',
  dataCy = 'employee-tips-payout-list-item',
  includedPayouts,
  isLoading,
  maxTipAmount,
  payee,
  onCashEdit,
  onConnectEmployee,
  onEditHoldback,
  onNoteChange,
  currentSite,
  creditCardFee = 0,
}) => {
  const header = buildHeader(payee, currentSite?.role === SiteRole.ADMIN, maxTipAmount, onConnectEmployee, onCashEdit);

  return (
    <Layout className={className}>
      <EmployeePayoutAccordion payee={payee} editHoldbackTrigger={<EditHoldbackTrigger data-cy="edit-holdback" label="Edit" onClick={() => onEditHoldback?.(payee)} />} includedPayouts={includedPayouts} header={header} isLoading={isLoading}>
        <CalculationBreakdownList calculations={payee.calculations} calculatorRules={currentCalculatorRules.rules} categoryGroups={currentCalculatorRules.categoryGroups} segments={payee.segments} creditCardFee={creditCardFee} />
        <NoteWrapper>
          <NoteInput type="text" data-cy={`${dataCy}-note-input`} maxLength={100} placeholder="Enter an optional note." defaultValue={payee.note} onBlur={(e) => onNoteChange?.(payee.id, e.currentTarget.value)} />
        </NoteWrapper>
      </EmployeePayoutAccordion>
    </Layout>
  );
};

export default EmployeeListItem;
